import { useState } from 'react';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';

/**
 * Shared hook for list-page CRUD flow: form modal, confirm dialog (update/delete), success dialog.
 * Use with any entity that has create/update/delete mutations and an edit form with optional update confirmation.
 *
 * @param {Object} config
 * @param {import('@tanstack/react-query').UseMutationResult} config.createMutation
 * @param {import('@tanstack/react-query').UseMutationResult} config.updateMutation - mutate({ id, payload })
 * @param {import('@tanstack/react-query').UseMutationResult} config.deleteMutation - mutate(id)
 * @param {(item: unknown) => string} config.getItemId
 * @param {(item: unknown) => string} config.getItemName
 * @param {Object} config.messages - { createSuccess, createError, updateSuccess, updateError, updateMissingId, deleteSuccess, deleteError }
 * @returns {Object} Form, confirm, and success state + handlers for the page and modals.
 */
export function useCrudModals({
  createMutation,
  updateMutation,
  deleteMutation,
  getItemId,
  getItemName,
  messages,
}) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formError, setFormError] = useState('');
  const [confirmState, setConfirmState] = useState({ type: null, data: null });
  const [successState, setSuccessState] = useState({ isOpen: false, message: '', variant: 'success' });
  const [pendingPayload, setPendingPayload] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const confirmLoading =
    confirmState.type === 'update'
      ? updateMutation.isPending
      : confirmState.type === 'delete'
        ? deleteMutation.isPending
        : false;
  const deletingId = actionLoading?.type === 'delete' ? actionLoading.id : null;

  const openCreateModal = () => {
    setFormMode('create');
    setSelectedItem(null);
    setFormError('');
    setIsFormOpen(true);
  };

  const openEditModal = (item) => {
    setFormMode('edit');
    setSelectedItem(item);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
    setSelectedItem(null);
    setFormMode('create');
    setFormError('');
    setPendingPayload(null);
    setConfirmState({ type: null, data: null });
    createMutation.reset();
    updateMutation.reset();
  };

  const handleSuccessClose = () => {
    const variant = successState.variant;
    setSuccessState({ isOpen: false, message: '', variant: 'success' });
    if (variant === 'error') {
      createMutation.reset();
      updateMutation.reset();
      deleteMutation.reset();
    }
  };

  const openSuccess = (message, variant = 'success') => {
    setSuccessState({ isOpen: true, message, variant });
  };

  const handleSubmitForm = ({ mode, payload }) => {
    setFormError('');

    if (formMode === 'create') {
      createMutation.mutate(payload, {
        onSuccess: (response) => {
          setIsFormOpen(false);
          setSuccessState({
            isOpen: true,
            message: response?.message ?? messages.createSuccess,
            variant: 'success',
          });
        },
        onError: (err) => {
          console.error('[Create form]', err?.response?.data ?? err?.message ?? err);
          setSuccessState({
            isOpen: true,
            message: getApiErrorMessage(err, messages.createError),
            variant: 'error',
          });
        },
      });
      return;
    }

    const id = selectedItem ? getItemId(selectedItem) : null;
    if (!id) {
      setFormError(messages.updateMissingId);
      return;
    }

    setPendingPayload(payload);
    setConfirmState({
      type: 'update',
      data: { id, name: getItemName(selectedItem) },
    });
  };

  const handleConfirmCancel = () => {
    const prev = confirmState.type;
    setConfirmState({ type: null, data: null });
    if (prev === 'update') setPendingPayload(null);
  };

  const handleConfirmUpdate = () => {
    if (!confirmState.data?.id || !pendingPayload) return;
    updateMutation.mutate(
      { id: confirmState.data.id, payload: pendingPayload },
      {
        onSuccess: (response) => {
          setConfirmState({ type: null, data: null });
          setPendingPayload(null);
          setIsFormOpen(false);
          setSelectedItem(null);
          setSuccessState({
            isOpen: true,
            message: response?.message ?? messages.updateSuccess,
            variant: 'success',
          });
        },
        onError: (err) => {
          setConfirmState({ type: null, data: null });
          setPendingPayload(null);
          console.error('[Update form]', err?.response?.data ?? err?.message ?? err);
          setSuccessState({
            isOpen: true,
            message: getApiErrorMessage(err, messages.updateError),
            variant: 'error',
          });
        },
      }
    );
  };

  const handleDeleteRequest = (item) => {
    setConfirmState({
      type: 'delete',
      data: { id: getItemId(item), name: getItemName(item) },
    });
  };

  const handleConfirmDelete = () => {
    const id = confirmState.data?.id;
    if (!id) return;
    setActionLoading({ type: 'delete', id });
    deleteMutation.mutate(id, {
      onSuccess: (response) => {
        setConfirmState({ type: null, data: null });
        setSuccessState({
          isOpen: true,
          message: response?.message ?? messages.deleteSuccess,
          variant: 'success',
        });
      },
      onError: (err) => {
        setConfirmState({ type: null, data: null });
        setSuccessState({
          isOpen: true,
          message: getApiErrorMessage(err, messages.deleteError),
          variant: 'error',
        });
      },
      onSettled: () => setActionLoading(null),
    });
  };

  const handleConfirm = () => {
    if (confirmState.type === 'delete') handleConfirmDelete();
    else handleConfirmUpdate();
  };

  return {
    form: {
      isFormOpen,
      formMode,
      selectedItem,
      formError,
      setFormError,
      openCreateModal,
      openEditModal,
      handleCloseForm,
      handleSubmitForm,
      isSubmitting,
      openSuccess,
    },
    confirm: {
      isOpen: Boolean(confirmState.type),
      type: confirmState.type,
      data: confirmState.data,
      isLoading: confirmLoading,
      onConfirm: handleConfirm,
      onCancel: handleConfirmCancel,
    },
    success: {
      ...successState,
      onClose: handleSuccessClose,
    },
    deletingId,
    deleteMutation,
    handleDeleteRequest,
  };
}

export default useCrudModals;
